import { type FC } from 'react'
import { type FormikHelpers } from 'formik'
import { useTranslation } from 'react-i18next'

import { useGetProfileLastAddedAddress } from '~/profile/model/profile/hooks.js'
import {
  ToDoorPreferenceForm,
  type ToDoorPreferenceFormValues,
  useCreateToDoorPreferenceValidationSchema,
} from '~/profile/view/preferences/setup/components/ToDoorPreferenceForm.js'
import { useSubmitToDoorPreference } from '~/profile/view/preferences/setup/hooks/useSubmitToDoorPreference.js'
import { useSetTitle, useWizardContext } from '~/setup/model/context.js'
import { StepForm } from '../StepForm.js'
import { useGetAgreedPlacePreference } from '~/profile/view/preferences/hooks/useGetAgreedPlacePreference.js'
import { ToDoorPreferenceSkeleton } from '~/profile/view/preferences/setup/ToDoorPreferenceSetupPage.js'

export const DeliveryPreferenceToDoor: FC = () => {
  const { t } = useTranslation()
  const { onStepComplete } = useWizardContext()
  const { validationSchema } = useCreateToDoorPreferenceValidationSchema()
  const { data: lastAddedAddress } = useGetProfileLastAddedAddress()
  const addressId = lastAddedAddress?.addressId
  const { initialFormValues, isLoading, preference } = useGetAgreedPlacePreference(addressId)

  const { submitToDoorPreference } = useSubmitToDoorPreference(addressId ?? '')

  useSetTitle(t('portal.profile.preferences.options.DoorDelivery.title'))

  const handleSubmit = async (
    values: ToDoorPreferenceFormValues,
    helpers: FormikHelpers<ToDoorPreferenceFormValues>
  ) => {
    if (!addressId) {
      helpers.setSubmitting(false)
      return
    }

    try {
      await submitToDoorPreference(values)
      onStepComplete()
    } catch {
      helpers.setSubmitting(false)
    }
  }

  const initialValues: ToDoorPreferenceFormValues = {
    ...initialFormValues,
    fallback: preference?.activeSetting?.fallback?.preferenceType ?? 'NoPreference',
  }

  return (
    <StepForm
      enableReinitialize
      validateOnChange
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {isLoading && <ToDoorPreferenceSkeleton />}
      {!isLoading && <ToDoorPreferenceForm />}
    </StepForm>
  )
}
